import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { promptsApi } from '@/services/api';
import type { CreatePromptInput, UpdatePromptInput } from '@/types';

export const usePrompts = (page = 1, limit = 20, filters?: { isActive?: string; search?: string }) => {
  return useQuery({
    queryKey: ['prompts', page, limit, filters],
    queryFn: () => promptsApi.getAll(page, limit, filters).then(res => res.data),
  });
};

export const usePrompt = (id: string | undefined) => {
  return useQuery({
    queryKey: ['prompt', id],
    queryFn: () => promptsApi.getById(id!).then(res => res.data.data),
    enabled: !!id,
  });
};

export const useCreatePrompt = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreatePromptInput) =>
      promptsApi.create(data).then(res => res.data.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['prompts'] });
    },
  });
};

export const useUpdatePrompt = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdatePromptInput }) =>
      promptsApi.update(id, data).then(res => res.data.data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['prompts'] });
      queryClient.invalidateQueries({ queryKey: ['prompt', variables.id] });
    },
  });
};

export const useDeletePrompt = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => promptsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['prompts'] });
    },
  });
};

export const usePromptAgents = (promptId: string | undefined) => {
  return useQuery({
    queryKey: ['prompt-agents', promptId],
    queryFn: () => promptsApi.getAgentsUsingPrompt(promptId!).then(res => res.data.data),
    enabled: !!promptId,
  });
};
